/**
 * `CertificationDetail` — pagina di dettaglio di una singola certificazione,
 * raggiungibile dalla vista "Livelli → Certificazioni" cliccando il nome di una
 * riga della `CertificationLevelsView`.
 *
 * Mostra:
 *  - una card "hero" con l'immagine grande del badge, il nome, l'ente (issuer)
 *    e il link esterno alla pagina/badge (se presenti);
 *  - la descrizione della certificazione e le skill collegate (chip), quando
 *    valorizzate;
 *  - una tabella delle persone che l'hanno ottenuta (nome, reparto, origine,
 *    date di conseguimento/scadenza).
 *
 * Dati: `GET /api/certifications/:id/detail` → `CertificationDetailDto` (rotta
 * protetta lato API da `requireLevelsAccess`, coerentemente con la sezione
 * "Livelli"). La query è abilitata solo quando l'`:certificationId` è presente.
 */
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import type {
  CertificationDetailDto,
  CertificationHolderDto,
} from '../shared/dto';
import { certExpiryStatus, formatCertDate } from '../components/CertificationCard';
import { CertificationMedal } from '../components/CertificationMedal';
import { downloadBadgeImage } from '../utils/downloadBadge';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  Icon,
  Loading,
  PageHeader,
  type Column,
} from '../components/ui';

/** Renderizza la scadenza con lo stesso stato/tono della card certificazione. */
function ExpiryValue({ iso }: { iso: string | null }) {
  const status = certExpiryStatus(iso);
  if (status === 'none') return <>Nessuna</>;
  if (status === 'expired') {
    return <Badge tone="danger">{formatCertDate(iso)}</Badge>;
  }
  if (status === 'expiring') {
    return <Badge tone="warning">In scadenza {formatCertDate(iso)}</Badge>;
  }
  return <>{formatCertDate(iso)}</>;
}

export default function CertificationDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { certificationId } = useParams<{ certificationId: string }>();

  const { data, isLoading, isError, error } = useQuery<CertificationDetailDto>({
    queryKey: ['certifications', 'detail', certificationId],
    queryFn: () =>
      api.get<CertificationDetailDto>(
        `/api/certifications/${encodeURIComponent(certificationId ?? '')}/detail`,
      ),
    enabled: certificationId !== undefined && certificationId !== '',
  });

  const backToLevels = () => {
    void navigate('/insights?view=certifications');
  };

  const columns = useMemo<Column<CertificationHolderDto>[]>(
    () => [
      {
        key: 'displayName',
        header: 'Persona',
        sortable: true,
        sortAccessor: (row) => row.displayName.toLowerCase(),
        render: (row) => (
          <div className="coverage-name">
            <Link
              to={`/insights/team/${encodeURIComponent(row.userId)}`}
              className="coverage-name__link"
              aria-label={`Apri la dashboard di ${row.displayName}`}
            >
              {row.displayName}
              <Icon name="external" size={14} className="coverage-name__link-icon" />
            </Link>
            {row.departmentName !== null && (
              <Badge tone="neutral">{row.departmentName}</Badge>
            )}
          </div>
        ),
      },
      {
        key: 'source',
        header: 'Origine',
        sortable: true,
        sortAccessor: (row) => row.source,
        render: (row) =>
          row.source === 'credly' ? (
            <Badge tone="info">Credly</Badge>
          ) : (
            <Badge tone="neutral">Manuale</Badge>
          ),
      },
      {
        key: 'issuedAt',
        header: 'Conseguita',
        sortable: true,
        sortAccessor: (row) => row.issuedAt ?? '',
        render: (row) => formatCertDate(row.issuedAt),
      },
      {
        key: 'expiresAt',
        header: 'Scadenza',
        sortable: true,
        sortAccessor: (row) => row.expiresAt ?? '',
        render: (row) => {
          const status = certExpiryStatus(row.expiresAt);
          if (status === 'none') return 'Nessuna';
          if (status === 'expired') {
            return (
              <Badge tone="danger">{formatCertDate(row.expiresAt)}</Badge>
            );
          }
          if (status === 'expiring') {
            return (
              <Badge tone="warning">In scadenza {formatCertDate(row.expiresAt)}</Badge>
            );
          }
          return formatCertDate(row.expiresAt);
        },
      },
    ],
    [],
  );

  if (isLoading) {
    return <Loading>Caricamento certificazione…</Loading>;
  }

  if (isError || data === undefined) {
    const message =
      error instanceof Error
        ? error.message
        : 'Impossibile caricare la certificazione.';
    return (
      <section
        className="certification-detail"
        aria-labelledby="certification-detail-title"
      >
        <PageHeader
          title="Dettaglio certificazione"
          titleId="certification-detail-title"
          actions={
            <Button type="button" variant="ghost" onClick={backToLevels}>
              ← Torna alle certificazioni
            </Button>
          }
        />
        <Alert tone="error">{message}</Alert>
      </section>
    );
  }

  const isCompany = data.isCompany === true;

  return (
    <section
      className="certification-detail"
      aria-labelledby="certification-detail-title"
    >
      <PageHeader
        title="Dettaglio certificazione"
        titleId="certification-detail-title"
        subtitle={
          isCompany
            ? "I dettagli della certificazione, l'ente che la rilascia e le date."
            : "Chi la possiede, l'ente che la rilascia e le skill collegate."
        }
        actions={
          <>
            <Button type="button" variant="ghost" onClick={backToLevels}>
              ← Torna alle certificazioni
            </Button>
            {data.imageUrl !== undefined && (
              <Button
                type="button"
                variant="default"
                onClick={() => {
                  void downloadBadgeImage(data.imageUrl as string, data.name);
                }}
              >
                <Icon name="download" />
                <span>Scarica il badge</span>
              </Button>
            )}
          </>
        }
      />

      {/* Card "hero": unico box con l'immagine grande (che sborda dal riquadro),
          il nome, l'ente, la descrizione, le skill collegate e il link esterno.
          Per le aziendali il retro della medaglia mostra le date (Conseguita/
          Scadenza) come nel dettaglio personale; per le personali il numero di
          possessori. */}
      <div className="certification-detail__hero">
        <CertificationMedal
          imageUrl={data.imageUrl}
          name={data.name}
          issuer={data.issuer}
          backFields={
            isCompany
              ? [
                  {
                    label: 'Conseguita',
                    value: formatCertDate(data.issuedAt ?? null),
                  },
                  {
                    label: 'Scadenza',
                    value:
                      data.expiresAt == null
                        ? 'Nessuna'
                        : formatCertDate(data.expiresAt),
                  },
                ]
              : [
                  {
                    label: 'Possessori',
                    value: String(data.people),
                  },
                ]
          }
        />
        <div className="certification-detail__heading">
          <h2 className="certification-detail__name">{data.name}</h2>
          {data.issuer !== undefined && (
            <p className="certification-detail__issuer">{data.issuer}</p>
          )}
          {!isCompany && (
            <p className="certification-detail__people">
              {data.people === 1
                ? '1 persona la possiede'
                : `${data.people} persone la possiedono`}
            </p>
          )}
          {data.description !== undefined && (
            <p className="certification-detail__description">{data.description}</p>
          )}
          {data.skills.length > 0 && (
            <div className="certification-detail__skills">
              {data.skills.map((skill) => (
                <Badge key={skill.id} tone="neutral">
                  {skill.name}
                </Badge>
              ))}
            </div>
          )}
          {data.url !== undefined && data.url.trim() !== "" && (
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="certification-detail__link"
            >
              <Icon name="external" />
              <span>Vedi la certificazione</span>
            </a>
          )}

          {/* Aziendale: date della certificazione (come nel dettaglio
              personale, ma senza origine/badge, che non hanno senso qui). */}
          {isCompany && (
            <dl className="certification-detail__meta">
              <div className="certification-detail__meta-item">
                <dt>Conseguita</dt>
                <dd>{formatCertDate(data.issuedAt ?? null)}</dd>
              </div>
              <div className="certification-detail__meta-item">
                <dt>Scadenza</dt>
                <dd>
                  <ExpiryValue iso={data.expiresAt ?? null} />
                </dd>
              </div>
            </dl>
          )}
        </div>
      </div>

      {!isCompany && (
        <DataTable
          columns={columns}
          rows={data.holders}
          rowKey={(row) => row.userId}
          defaultSort={{ key: 'displayName', direction: 'asc' }}
          emptyMessage="Nessuna persona possiede ancora questa certificazione."
        />
      )}
    </section>
  );
}
